import { Injectable } from '@angular/core';
import {
    PlatformDataService,
    StorageDataService,
    UserInfoService,
    ShellDomainProvider,
    SqlQueryBuilderService,
    UtilityService
} from '../../core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { switchMap, map } from 'rxjs/operators';
import { map as _map } from 'lodash';

@Injectable()
export class UAMProfileImageUploadService {
    header: any = new HttpHeaders({
        'Content-Type': 'application/json'
    });

    constructor(
        private storageDataService: StorageDataService,
        private platformDataService: PlatformDataService,
        private _UserInfoService: UserInfoService,
        private _SqlQueryBuilderService: SqlQueryBuilderService,
        private utilityService: UtilityService,
        private http: HttpClient
    ) {}

    uploadProfileImageOfUser(userInfo, profileImageFile, uamConfig): any {
        return this.getPersonIdByUserId(userInfo && userInfo.ItemId).pipe(
            switchMap((responsePersonId: any) => {
                return this.uploadProfileImage(responsePersonId, profileImageFile, uamConfig);
            }),
            map(response => response)
        );
    }

    uploadProfileImage(personId: string, imageFile, uamConfig: any, fileId?: string): Observable<any> {
        let fileItemId = fileId ? fileId : this.utilityService.getNewGuid();
        return this.storageDataService.getPreSignedUrl(fileItemId, imageFile && imageFile['name'], null, [], null).pipe(
            switchMap((responsePresignedUrl: any) => {
                return this.uploadImageWithPresignedUrl(
                    personId,
                    responsePresignedUrl && responsePresignedUrl.body,
                    imageFile,
                    uamConfig
                );
            }),
            map(response => response)
        );
    }

    uploadImageWithPresignedUrl(personId, presignedUrl, file, uamConfig): Observable<any> {
        return this.storageDataService.uploadPresignedFile(presignedUrl && presignedUrl.UploadUrl, file).pipe(
            switchMap((response: any) => {
                return this.updatePersonProfileImage(personId, presignedUrl && presignedUrl.FileId);
            }),
            switchMap(response => {
                if (uamConfig && uamConfig.ProfileImageConfig && uamConfig.ProfileImageConfig.ResizeImage) {
                    return this.resizeProfileImageOfPerson(personId, presignedUrl.FileId, uamConfig);
                }
                return of(response);
            }),
            map(response => response)
        );
    }

    resizeProfileImageOfPerson(personId, fileId, uamConfig): Observable<any> {
        return this.resizeProfileImage(personId, fileId, uamConfig).pipe(map(response => response));
    }

    updatePersonProfileImage(PersonId, FileId) {
        let data = {
            ItemId: PersonId,
            ProfileImageId: FileId
        };

        return this.platformDataService.update('Person', data).pipe(
            map((ProfileImageUpdateResp: any) => {
                return ProfileImageUpdateResp && ProfileImageUpdateResp.body;
            })
        );
    }

    resizeProfileImage(PersonId, FileId: string, uamConfig: any): Observable<any> {
        return this.removeProfileImageConnection(PersonId, uamConfig).pipe(
            switchMap((response: any) => {
                let payload = this.getFileConversionPayload(FileId, PersonId, uamConfig);
                return this.http.post(
                    ShellDomainProvider['api'].Storage + 'StorageCommand/ExecuteFileConversionPipeline',
                    payload,
                    { headers: this.header, withCredentials: true, observe: 'response' }
                );
            }),
            map((response: any) => response)
        );
    }

    getFileConversionPayload(fileId: string, personId: string, uamConfig: any) {
        let Dimensions =
            uamConfig && uamConfig.ProfileImageConfig && uamConfig.ProfileImageConfig.ProfileImageDimensions;
        let tagPrefix =
            (uamConfig && uamConfig.ProfileImageConfig && uamConfig.ProfileImageConfig.TagPrefix) || 'Person-Profile';
        return {
            ConversionPipelineId: 'ECAP-IRPL-v.1',
            TaskId: this.utilityService.getNewGuid(),
            RequestProperties: {
                ImageResizeSetting: JSON.stringify({
                    SourceFileId: fileId,
                    Dimensions: Dimensions,
                    ParentEntities: [{ Id: personId, Name: 'Person', TagPrefix: tagPrefix }]
                })
            }
        };
    }

    removeProfileImageConnection(personId, uamConfig): Observable<any> {
        let Tags = [];
        let tagPrefix =
            (uamConfig && uamConfig.ProfileImageConfig && uamConfig.ProfileImageConfig.TagPrefix) || 'Person-Profile';
        uamConfig &&
            uamConfig.ProfileImageConfig &&
            uamConfig.ProfileImageConfig.ProfileImageDimensions.forEach(item => {
                Tags.push(`${tagPrefix}-${item.Width}-${item.Height}`);
            });

        let connectionQuery = this._SqlQueryBuilderService.prepareGetConnectionQuery(
            'Person',
            personId,
            'File',
            '',
            true,
            Tags,
            false,
            false
        );

        return this.platformDataService.getConnections(connectionQuery).pipe(
            switchMap((ConnectionResp: any) => {
                let Connections =
                    ConnectionResp.body && ConnectionResp.body.StatusCode === 0 ? ConnectionResp.body.Results : [];
                return this.disconnectAllConnection(Connections);
            }),
            map((Response: any) => {
                if (Response.Status) {
                    return { Status: true };
                } else {
                    return { Status: false };
                }
            })
        );
    }

    private disconnectAllConnection(Connections: any[]): Observable<any> {
        if (Connections.length) {
            let HttpCalls = this.getDisconnectHttpCalls(Connections);
            return this.http
                .post(ShellDomainProvider['api'].AggregateService + 'Execute', HttpCalls, {
                    headers: this.header,
                    withCredentials: true,
                    observe: 'response'
                })
                .pipe(
                    map((response: any) => {
                        if (response.status === 200) {
                            return { Status: true };
                        } else {
                            return { Status: false };
                        }
                    })
                );
        } else {
            return of({ Status: false });
        }
    }

    private getDisconnectHttpCalls(Connections: any[]) {
        let HttpCalls = [];
        Connections.forEach((Connection: any) => {
            HttpCalls.push(this.getProfileResourceDisconnectPayload(Connection.ItemId));
        });
        return { HttpCalls: HttpCalls };
    }

    private getProfileResourceDisconnectPayload(ConnectionId) {
        let payloadObj = {
            Uri: ShellDomainProvider['api'].DataCore + `DataManipulationCommand/Disconnect`,
            Verb: 'Post',
            SuccessIf: '{ "StatusCode":0 }',
            Payload: JSON.stringify({ ItemId: ConnectionId })
        };
        payloadObj.Uri = payloadObj.Uri.replace('https://', 'http://');

        return payloadObj;
    }
    getPersonIdByUserId(userId): any {
        return this._UserInfoService.getPersonConnection(userId).pipe(map(responsePersonId => responsePersonId));
    }

    getProfileImageOfPerson(profileImageId) {
        return this.storageDataService
            .getFileInfo(profileImageId)
            .pipe(map((response: any) => response && response.body));
    }
    getProfileImageResourse(userList, tag): any {
        let personFilterQuery = [];
        let userIds = _map(userList.Users, 'ItemId');
        personFilterQuery.push({
            property: 'CreatedBy',
            operator: '|',
            value: userIds && userIds.join()
        });
        return this.getPersonsOfUsers(personFilterQuery, ['ItemId', 'CreatedBy']).pipe(
            switchMap((responsePersons: any) => {
                return this.getImageResourse(responsePersons, tag);
            }),
            map(responseProfileImages => {
                return this.setProfileImageOfUser(userList, responseProfileImages);
            })
        );
    }

    getPersonsOfUsers(personFilterQuery, personsProperty): any {
        let query = this._SqlQueryBuilderService.prepareQuery(
            'Person',
            personsProperty,
            personFilterQuery,
            null,
            0,
            100
        );
        return this.platformDataService.getBySqlFilter('Person', query).pipe(
            map((responsePersonData: any) => {
                if (responsePersonData && responsePersonData.body) {
                    return responsePersonData.body.Results;
                } else {
                    return null;
                }
            })
        );
    }
    getImageResourse(persons, tag): any {
        let payloads = [];
        persons &&
            persons.forEach(person => {
                payloads.push({
                    EntityName: 'Person',
                    ItemId: person.ItemId,
                    Tag: tag
                });
            });
        return this.storageDataService.getResources(payloads).pipe(
            map((response: any) => {
                return response && this.setProfileImageOfPerson(persons, response);
            })
        );
    }

    setProfileImageOfPerson(persons, imageFiles) {
        persons &&
            persons.forEach(person => {
                imageFiles &&
                    imageFiles.forEach(file => {
                        if (person.ItemId == file.EntityId && file.Uri) {
                            person.Uri = file.Uri;
                        }
                    });
            });
        return persons;
    }

    setProfileImageOfUser(userList, personWithImage) {
        userList &&
            userList.Users &&
            userList.Users.forEach(user => {
                personWithImage &&
                    personWithImage.forEach(person => {
                        if (user.ItemId == person.CreatedBy && person.Uri) {
                            user.Uri = person.Uri;
                        }
                    });
            });
        return userList;
    }

    getProfileImageFiles(userList): any {
        let personFilterQuery = [];
        let userIds = _map(userList.Users, 'ItemId');
        personFilterQuery.push({
            property: 'CreatedBy',
            operator: '|',
            value: userIds && userIds.join()
        });
        return this.getPersonsOfUsers(personFilterQuery, ['ItemId', 'CreatedBy', 'ProfileImageId']).pipe(
            switchMap((responsePerson: any) => {
                return this.getProfileImageFileOfPerson(responsePerson, userList);
            }),
            map(response => response)
        );
    }
    getProfileImageFileOfPerson(personList, userList): any {
        let profileImageIds = _map(personList, 'ProfileImageId');
        return this.storageDataService.getFilesInfo(profileImageIds).pipe(
            map((responseProfileImageFiles: any) => {
                return this.getUserInfoWithProfileImage(
                    userList,
                    personList,
                    responseProfileImageFiles && responseProfileImageFiles.body
                );
            })
        );
    }
    getUserInfoWithProfileImage(userList, personList, profileImageFiles) {
        profileImageFiles &&
            profileImageFiles.forEach(file => {
                personList &&
                    personList.forEach(person => {
                        if (person.ProfileImageId == file.ItemId) {
                            person.Uri = file.Url;
                        }
                    });
            });
        userList &&
            userList.Users &&
            userList.Users.forEach(user => {
                personList &&
                    personList.forEach(person => {
                        if (person.CreatedBy == user.ItemId && person.Uri) {
                            user.Uri = person.Uri;
                        }
                    });
            });
        return userList;
    }
}
